import { useCallback, useState, useEffect } from 'react';

import { useStatistics } from '@/hooks/useStatistics';
import { useQuestionnaireNavigation } from '@/context/NavigationProvider';

import ResearchIllustrationUrl from '@/assets/ResearchIllustration.png';
import { Button } from '@/components/ui/button';
import { useSelectedPatient, useUpdatePatient } from '@/hooks/patient.hooks';
import { PatientPageLayout } from '@/components/PatientPageLayout';

import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { RxCross1 } from 'react-icons/rx';
import { scrollToTop } from '@/util/scroll';

const ExplanationModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => (
  <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-h-[95vh] nsm:w-[452px]">
      <div className="flex items-start justify-between rounded-t p-2">
        <div />
        <DialogClose
          className="absolute top-3 right-2 h-8 rounded-lg p-1 mr-2 cursor-pointer focus:ring-0 focus:outline-hidden focus:border-transparent hover:bg-gray-100"
          onClick={onClose}
        >
          <RxCross1 className="h-5 w-5 text-gray-600 stroke-[0.2]" />
        </DialogClose>
      </div>
      <div className="flex-1 overflow-auto px-8 md:px-10 overflow-y-auto no-scrollbar">
        <div className="text-left flex flex-col items-center">
          <div className="font-bold text-3.5xl pb-4 leading-tight mt-4">
            <span>{'Aidez-nous à améliorer notre service'}</span>
          </div>
          <div className="flex flex-col gap-4 text-black pb-8">
            <div>
              <p className="font-bold ">{"De quoi s'agit-il ?"}</p>
              <p className="text-sm mt-2">
                {
                  "Vous pouvez choisir de partager vos réponses désidentifiées à ce questionnaire avec l'équipe d'Aldebaran afin de nous aider à améliorer notre service. Ce traitement est entièrement facultatif et ne sera effectué qu'avec votre consentement."
                }
              </p>
            </div>
            <div>
              <p className="font-bold ">Quelles données seront partagées ?</p>
              <p className="text-sm mt-2">
                {
                  'Uniquement les questions et réponses de ce questionnaire sans vos données identifiantes telles que votre nom, date de naissance, et les informations sur votre RDV. Ces données seront stockées de la même manière que nous stockons toutes les données de nos utilisateurs, sur des serveurs certifiés HDS en France comme décrit dans notre '
                }
                <a
                  className="underline"
                  href="https://aldebaran.care/legal/politique-protection-donnees"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  politique de protection des données
                </a>
                .
              </p>
            </div>
            <div>
              <p className="font-bold ">Pourquoi faire ?</p>
              <p className="text-sm mt-2">
                {
                  "Ces données nous aident à mieux comprendre les besoins de nos utilisateurs et à améliorer notre service. Par exemple, nous pouvons identifier des questions qui sont difficiles à comprendre ou des réponses qui sont peu claires. Cela nous aide également à identifier les sujets de santé qui nécessitent plus de développement afin d'aider des futurs patients."
                }
              </p>
            </div>
            <div>
              <p className="font-bold ">Et si je refuse ?</p>
              <p className="text-sm mt-2">
                Pas de problème ! Ce partage est complètement facultatif. Si
                vous ne souhaitez pas participer, vos réponses à ce
                questionnaire seront toujours transmises à votre médecin (et
                uniquement votre médecin).
              </p>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const ResearchConsent = () => {
  const { navigateNext } = useQuestionnaireNavigation();
  const [open, setOpen] = useState(false);

  const { data: patient } = useSelectedPatient();
  const { mutate: recordConsent } = useUpdatePatient();

  const recordStatistics = useStatistics();

  useEffect(() => {
    scrollToTop();
  }, []);

  const handleConsent = useCallback(
    (researchConsent: boolean) => {
      if (patient) {
        recordStatistics({
          section: researchConsent
            ? 'RESEARCH_CONSENT_ACCEPTED'
            : 'RESEARCH_CONSENT_REFUSED',
        });
        recordConsent(
          { id: patient.id, researchConsent },
          {
            onError: () =>
              console.log(
                'Error recording research consent, assuming no consent given',
              ),
          },
        );

        navigateNext();
      }
    },
    [recordConsent, patient, navigateNext, recordStatistics],
  );

  return (
    <div className="flex h-full flex-col">
      <PatientPageLayout
        title={
          <div className="flex items-center justify-center">
            <img
              src={ResearchIllustrationUrl}
              alt="Research illustration"
              className="w-85 nsm:mb-0 mb-2"
            />
          </div>
        }
        titleClassName="text-center"
        footer={
          <div className="flex w-full max-w-[330px] flex-col items-center gap-4 mx-auto">
            <Button
              variant="ctaPrimary"
              size="lg"
              onClick={() => handleConsent(true)}
            >
              Accepter
            </Button>
            <Button
              variant="noBorder"
              size="lg"
              onClick={() => handleConsent(false)}
            >
              Refuser
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center gap-4 px-2 font-light text-sm">
          <h1 className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center leading-tight">
            Aidez-nous à améliorer Aldebaran pour vous et vos soignants
          </h1>
          <p className="leading-5 w-full nxs:text-center">
            Afin d&apos;améliorer notre solution et de la rendre encore plus
            utile pour les patients et les soignants, nous pourrions utiliser
            certaines de vos données après les avoir désidentifiées (sans
            information permettant de vous identifier).
          </p>
          <button
            onClick={() => setOpen(true)}
            className="underline py-3 pb-2 text-sm w-full nxs:text-center cursor-pointer"
          >
            En savoir plus sur l&apos;utilisation de mes informations
          </button>
        </div>
      </PatientPageLayout>
      <ExplanationModal isOpen={open} onClose={() => setOpen(false)} />
    </div>
  );
};
